//
// # Instance Drawings
//

// Local Imports
import { model, Point } from "GenAlphaModel";
import { EntityKind } from "../../../entity";
import { ResolvedInstance } from "./resolved";
import { UnresolvedInstance } from "./unresolved";
import { MousePos } from "../../../mousepos";
import { Args, type Parent } from "../../../tree";
import { Base } from "./base";
import { DrawTo } from "../../../draw";

//
// # Instance
//
// This is the thing held in `Schematic.instances`.
// It wraps the concrete implementations `ResolvedInstance` and `UnresolvedInstance`,
// largely so that we can keep the same object in the `Schematic.instances` set
// if and when we go back and forth between the two sub-types.
//
export class Instance
  extends Base<model.schematic.Instance>
  implements model.schematic.Xformable
{
  readonly entityKind = EntityKind.Instance;
  private inner!: InstanceInner; // The concrete instance, either `Resolved` or `Unresolved`

  constructor(args: Args<model.schematic.Instance>) {
    super(args);
    this.build();
  }

  override build = () => {
    this.inner = InstanceInner.create(this.data, this);
  };

  // Bounding box transformed to schematic coordinates.
  // Prefer the resolved symbol's bbox (matches what's actually drawn);
  // `data.bbox` is a fallback for instances placed before symbol
  // resolution completes.
  get bbox(): model.Bbox {
    return model.Xform.transformBbox(
      this.xform,
      this.data.symbol?.bbox ?? this.data.bbox
    );
  }

  // Forward everything else to `inner`
  override hitTest = (mousePos: MousePos) => this.inner.hitTest(mousePos);
  override intersectsBbox = (bbox: model.Bbox) =>
    this.inner.intersectsBbox(bbox);
  override redraw = (to: DrawTo) => this.inner.redraw(to);

  // Get the world-coordinate locations of all pins on this instance.
  // Used for finding wires connected to this instance.
  // World-space bounding box of each pin's hit shape, index-aligned with
  // `getPinWorldLocations`. The circuit extractor decides an instance pin is
  // connected when a wire endpoint falls inside this box, so anything that
  // wants to agree with the netlist needs it rather than the centre alone.
  getPinWorldBboxes(): model.Bbox[] {
    const { symbol } = this.data;
    if (!symbol) return [];
    return symbol.pins.map((pin) =>
      model.Xform.transformBbox(
        this.xform,
        model.schematic.PinShape.bbox(pin.shape)
      )
    );
  }

  getPinWorldLocations(): Point[] {
    const { symbol } = this.data;
    if (!symbol) return [];

    return symbol.pins.map((pin) => {
      // Get the center of the pin shape in local (symbol) coordinates
      const localCenter = model.schematic.PinShape.bboxCenter(pin.shape);
      // Transform to world coordinates using instance xform
      return model.Xform.transform(this.xform, localCenter);
    });
  }
}

export type InstanceInner = ResolvedInstance | UnresolvedInstance;
export const InstanceInner = {
  //
  // Create either a `Resolved` or `Unresolved` instance, depending on whether `data.symbol` is defined.
  //
  create(data: model.Instance, parent: Parent): InstanceInner {
    // The TS compiler is very finicky about this unwrapping and undefined-checking of `symbol` here.
    // Just, do it like this, it works.
    const { symbol } = data;
    if (symbol) {
      return new ResolvedInstance({ data: { ...data, symbol }, parent });
    }
    // Otherwise render an unresolved instance
    return new UnresolvedInstance({ data, parent });
  },
};
